import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Pencil, RefreshCw, Sparkles, X } from 'lucide-react';
import { AiThinkingIndicator, Badge, cn, Input, Skeleton, Textarea } from '@bliks-io/ui';
import type { ProcessStepRecording } from '@bliks-io/contracts';
import { EntityHeader } from '@/components/processes/EntityHeader';

type PopulatedAssignee = Parameters<typeof EntityHeader>[0]['entity'];

interface EditableFieldProps {
  label: string;
  controller: FieldController;
  loading: boolean;
  multiline?: boolean;
  editAriaLabel: string;
  placeholder: string;
}

export function EditableField({
  label,
  controller,
  loading,
  multiline,
  editAriaLabel,
  placeholder: placeholderText
}: EditableFieldProps): ReactNode {
  const { t } = useTranslation();
  const { value, editing, setValue, startEdit, confirmEdit, cancelEdit } = controller;

  if (loading && !value) {
    return (
      <FieldShell label={label}>
        <Skeleton className={multiline ? 'h-20 w-full' : 'h-9 w-3/4'} />
      </FieldShell>
    );
  }

  if (editing) {
    return (
      <FieldShell label={label}>
        <div className="flex items-start gap-2">
          {multiline ? (
            <Textarea
              className="flex-1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={4}
            />
          ) : (
            <Input
              className="flex-1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
          )}
          <EditActionButtons
            confirmAriaLabel={t('stepRecording.review.confirmEdit')}
            cancelAriaLabel={t('stepRecording.review.cancelEdit')}
            onConfirm={confirmEdit}
            onCancel={cancelEdit}
          />
        </div>
      </FieldShell>
    );
  }

  const placeholder = <span className="text-muted-foreground italic">{placeholderText}</span>;

  return (
    <FieldShell label={label}>
      <div className={`flex ${multiline ? 'items-start' : 'items-center'} justify-between gap-2`}>
        {multiline ? (
          <p className="text-sm leading-relaxed">{value || placeholder}</p>
        ) : (
          <span className="font-medium">{value || placeholder}</span>
        )}
        <button
          type="button"
          aria-label={editAriaLabel}
          onClick={startEdit}
          className="text-muted-foreground hover:text-foreground p-1 shrink-0"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
    </FieldShell>
  );
}

function FieldShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium uppercase text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

function EditActionButtons({
  confirmAriaLabel,
  cancelAriaLabel,
  onConfirm,
  onCancel
}: {
  confirmAriaLabel: string;
  cancelAriaLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-1 shrink-0">
      <button
        type="button"
        aria-label={confirmAriaLabel}
        onClick={onConfirm}
        className="text-positive hover:text-positive-strong p-1"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label={cancelAriaLabel}
        onClick={onCancel}
        className="text-muted-foreground hover:text-foreground p-1"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export interface FieldController {
  value: string;
  editing: boolean;
  setValue: (v: string) => void;
  startEdit: () => void;
  confirmEdit: () => void;
  cancelEdit: () => void;
}

/**
 * Holds a user `override` separate from the AI-`generated` value so that a
 * mid-edit regenerate doesn't replace the textarea contents. `cancelEdit`
 * reverts to the savepoint so the previous draft is recoverable.
 */
export function useFieldController(generated: string): FieldController {
  const [override, setOverride] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [savepoint, setSavepoint] = useState<string | null>(null);

  const value = override ?? generated;

  return {
    value,
    editing,
    setValue: setOverride,
    startEdit: () => {
      setSavepoint(override);
      setOverride(override ?? generated);
      setEditing(true);
    },
    confirmEdit: () => setEditing(false),
    cancelEdit: () => {
      setOverride(savepoint);
      setEditing(false);
    }
  };
}

interface AiStepCardProps {
  /**
   * Pre-resolved populated assignee for the `EntityHeader` chip. Callers look
   * up the `EntityOrSuggestionRef` against their local user/team/system hash
   * before passing in — this card only renders, it doesn't resolve.
   */
  assignedTo?: PopulatedAssignee;
  assignedToModel: ProcessStepRecording['assignedToModel'];
  aiEnabled: boolean;
  /**
   * Whether the fields are seeded from a model-written summary. False while the
   * generation is still running, after it failed, and whenever AI is off — in
   * those cases the card shows the recorder's own text and carries no AI mark.
   */
  hasGeneratedContent: boolean;
  isGenerating: boolean;
  nameField: FieldController;
  descriptionField: FieldController;
  onRegenerate: () => void;
}

function AiSuggestionBadge({ isGenerating, label }: { isGenerating: boolean; label: string }) {
  return (
    <Badge colorScheme="green" className="absolute -top-3 left-4 gap-1.5">
      {isGenerating ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Sparkles className="h-3 w-3" />
      )}
      {label}
    </Badge>
  );
}

function AiRegenerateButton({
  onRegenerate,
  isGenerating,
  label
}: {
  onRegenerate: () => void;
  isGenerating: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onRegenerate}
      disabled={isGenerating}
      className="absolute -top-3 right-4 inline-flex h-6 w-6 items-center justify-center rounded-full border border-positive-solid/70 bg-white text-positive hover:bg-positive-surface disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={label}
      title={label}
    >
      <RefreshCw className={cn('h-3 w-3', isGenerating && 'animate-spin')} />
    </button>
  );
}

function StepSummaryThinkingIndicator() {
  const { t } = useTranslation();
  // Fixed, ordered phases — the summary indicator always narrates the same three
  // stages (evaluate recording → summarize step → refine wording) rather than
  // sampling alternates, so the copy stays predictable.
  const [phases] = useState(() => [
    t('thinkingPhases.stepSummary.start1'),
    t('thinkingPhases.stepSummary.middle1'),
    t('thinkingPhases.stepSummary.end1')
  ]);
  return <AiThinkingIndicator phases={phases} className="py-2" />;
}

export function AiStepCard({
  assignedTo,
  assignedToModel,
  aiEnabled,
  hasGeneratedContent,
  isGenerating,
  nameField,
  descriptionField,
  onRegenerate
}: AiStepCardProps) {
  const { t } = useTranslation();

  const showIndicator = isGenerating && !nameField.value && !descriptionField.value;

  return (
    <div className="relative">
      {aiEnabled ? (
        <>
          <AiSuggestionBadge
            isGenerating={isGenerating}
            label={t('stepRecording.review.bliksySuggestion')}
          />
          <AiRegenerateButton
            onRegenerate={onRegenerate}
            isGenerating={isGenerating}
            label={t('stepRecording.review.regenerate')}
          />
        </>
      ) : null}
      <div
        className={`bg-white rounded-lg p-4 w-[480px] space-y-3 ${
          aiEnabled ? 'border-2 border-positive-solid/70' : 'border border-border'
        }`}
        // The mark follows the displayed content, not the AI toggle: a card whose
        // generation failed or never ran shows hand-typed text and stays unmarked.
        // Accepted gap (plan 022): the mark survives in-dialog manual edits, and
        // the adopted step itself persists no provenance (D5).
        data-ai-generated={hasGeneratedContent ? 'true' : undefined}
      >
        {assignedTo ? (
          <>
            <EntityHeader entity={assignedTo} entityType={assignedToModel} />
            <div className="h-px w-full bg-border" />
          </>
        ) : null}

        {showIndicator ? (
          <StepSummaryThinkingIndicator />
        ) : (
          <>
            <EditableField
              label={t('stepRecording.review.titleLabel')}
              controller={nameField}
              loading={isGenerating}
              editAriaLabel={t('stepRecording.review.editTitle')}
              placeholder={
                aiEnabled
                  ? t('stepRecording.review.generating')
                  : t('stepRecording.review.titlePlaceholder')
              }
            />

            <EditableField
              label={t('stepRecording.review.descriptionLabel')}
              controller={descriptionField}
              loading={isGenerating}
              multiline
              editAriaLabel={t('stepRecording.review.editDescription')}
              placeholder={
                aiEnabled
                  ? t('stepRecording.review.generating')
                  : t('stepRecording.review.descriptionPlaceholder')
              }
            />
          </>
        )}
      </div>
    </div>
  );
}
